import { Dialog } from '@angular/cdk/dialog';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslocoModule } from '@jsverse/transloco';
import { PaymentMethodIconPipe } from '@rma/dashboard/billing/private/util-payment-icon-pipe';
import { PaymentMethod, PaymentMethodService } from '@rma/dashboard/billing/public/api-payment-methods';
import { applyWithDialogDefaults } from '@rma/dashboard/generic/util/material';
import { StaticImageUrlPipe } from '@rma/dashboard/generic/util/pipes/static-image-url';
import { getQueryStringParam } from '@rma/generic/ng/util';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { filter, take } from 'rxjs';
import { AddPaymentMethodDialogComponent } from '../orc-add-payment-method-dialog/add-payment-method-dialog.component';
import { FinalisePaymentMethodDialogComponent } from '../orc-finalise-payment-method-dialog/finalise-payment-method-dialog.component';

@Component({
  selector: 'rma-payment-methods',
  templateUrl: './payment-methods.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecimalPipe, TranslocoModule, MatButtonModule, MatDialogModule, RmaIconComponent, MatCheckboxModule, PaymentMethodIconPipe],
  providers: [
    StaticImageUrlPipe,
    // Re-provide Dialog and MatDialog so their captured injector is this component's,
    // allowing OVERLAY_DEFAULT_CONFIG to scope usePopover:false to only this dialog.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    Dialog,
    MatDialog,
  ],
  host: { class: 'block' },
})
export class PaymentMethodsComponent implements OnInit {
  @Input() public paymentMethods: PaymentMethod[] | null = null;
  @Output() refresh = new EventEmitter<void>();

  constructor(
    private readonly dialog: MatDialog,
    private readonly paymentMethodService: PaymentMethodService,
  ) {}

  ngOnInit(): void {
    const setupIntent = getQueryStringParam('setup_intent');

    if (setupIntent) {
      this.finalisePaymentMethod(setupIntent);
    }
  }

  public updatePaymentMethod() {
    this.dialog
      .open<AddPaymentMethodDialogComponent, void, boolean>(AddPaymentMethodDialogComponent, applyWithDialogDefaults({}))
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => x === true),
      )
      .subscribe(() => this.refresh.emit());
  }

  public finalisePaymentMethod(setupIntent: string) {
    const setupIntentSecret = getQueryStringParam('setup_intent_client_secret');
    const isDefault = getQueryStringParam('is_default') === 'true';

    this.dialog.open<FinalisePaymentMethodDialogComponent>(
      FinalisePaymentMethodDialogComponent,
      applyWithDialogDefaults({
        data: { setupIntent, setupIntentSecret, isDefault },
      }),
    );
  }

  public makeDefault(paymentMethod: PaymentMethod) {
    this.paymentMethodService.setDefaultPaymentMethod(paymentMethod.id).subscribe(
      (_) => this.refresh.emit(),
      (x) => alert(x.Message),
    );
  }

  public removePaymentMethod(paymentMethod: PaymentMethod) {
    this.paymentMethodService.removePaymentMethod(paymentMethod.id).subscribe(
      (_) => this.refresh.emit(),
      (x) => alert(x.Message),
    );
  }
}
